import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import {
  formatHoldQty,
  hasHoldQty,
  isHoldQtyTyping,
  toHoldQty,
  validateHoldQty
} from 'helpers/holdQty';

// Marks a free-text reason (no reason id) that is kept as it is.
const CURRENT_REASON = '__current__';

const initialForm = hold => {
  const reasonId = Number(hold?.holdreason_id) || 0;
  const dueDate = hold?.raw_due_date ? moment(hold.raw_due_date) : null;
  return {
    qty: hasHoldQty(hold?.qty) ? String(hold.qty) : '',
    dueDate: dueDate && dueDate.isValid() ? dueDate.toDate() : null,
    reason: reasonId ? String(reasonId) : hold?.reason ? CURRENT_REASON : ''
  };
};

// Edits an active hold: qty, delivery/cycle date and reason.
const EditHoldModal = ({ show, hold, reasons, onHide, onSave }) => {
  const [form, setForm] = useState(initialForm(hold));
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(initialForm(hold));
      setErrors({});
      setSaveError('');
      setIsSaving(false);
      setConfirming(false);
    }
  }, [show, hold]);

  // Keep the current reason selectable even if it is free text or no longer
  // in the active list.
  const reasonOptions = useMemo(() => {
    const options = (reasons || []).map(reason => ({
      value: String(reason.value),
      label: reason.key
    }));
    const reasonId = Number(hold?.holdreason_id) || 0;
    if (
      reasonId &&
      !options.some(option => option.value === String(reasonId))
    ) {
      options.unshift({
        value: String(reasonId),
        label: hold?.reason || `Reason #${reasonId}`
      });
    } else if (!reasonId && hold?.reason) {
      options.unshift({ value: CURRENT_REASON, label: hold.reason });
    }
    return options;
  }, [reasons, hold]);

  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
    setSaveError('');
  };

  // Save checks the fields, then asks for confirmation before anything is sent.
  const handleSubmit = e => {
    e.preventDefault();
    const nextErrors = {
      qty: validateHoldQty(form.qty),
      dueDate: form.dueDate ? '' : 'Delivery/Cycle Date is required',
      reason: form.reason ? '' : 'Hold Reason is required'
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;
    setConfirming(true);
  };

  const handleConfirm = async () => {
    setConfirming(false);
    setIsSaving(true);
    const message = await onSave(hold, {
      qty: toHoldQty(form.qty),
      due_date: moment(form.dueDate).format('YYYY-MM-DD'),
      // An unchanged free-text reason is left out so it stays as it is.
      ...(form.reason !== CURRENT_REASON
        ? { holdreason_id: Number(form.reason) }
        : {})
    });
    setIsSaving(false);
    if (message) setSaveError(message);
  };

  // What the confirmation lists: current value -> new value for each field.
  const initial = initialForm(hold);
  const reasonLabel = value =>
    reasonOptions.find(option => option.value === value)?.label || '—';
  const dateLabel = date => (date ? moment(date).format('MM/DD/YY') : '—');
  const summary = [
    {
      label: 'Hold Qty',
      from: formatHoldQty(hold?.qty),
      to: formatHoldQty(form.qty)
    },
    {
      label: 'Delivery/Cycle Date',
      from: dateLabel(initial.dueDate),
      to: dateLabel(form.dueDate)
    },
    {
      label: 'Hold Reason',
      from: reasonLabel(initial.reason),
      to: reasonLabel(form.reason)
    }
  ];

  return (
    <>
      <Modal centered show={show} onHide={isSaving ? undefined : onHide}>
        <Form noValidate onSubmit={handleSubmit}>
          <Modal.Header closeButton={!isSaving}>
            <Modal.Title className="fs-8">Edit Hold</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-3 text-700">
              Rx {hold?.rx_id}
              {hold?.drug ? ` · ${hold.drug}` : ''}
              {hold?.patient && (
                <>
                  <br />
                  <span className="fs-10 text-600">{hold.patient}</span>
                </>
              )}
            </p>
            {saveError && (
              <Alert variant="danger" className="py-2 fs-10">
                {saveError}
              </Alert>
            )}
            <Row className="g-3">
              <Col md={6}>
                <Form.Group controlId="edit-hold-qty">
                  <Form.Label className="required">Hold Qty</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 12 or 2.5"
                    autoFocus
                    value={form.qty}
                    isInvalid={Boolean(errors.qty)}
                    disabled={isSaving}
                    onChange={e => {
                      if (isHoldQtyTyping(e.target.value)) {
                        setField('qty', e.target.value);
                      }
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.qty}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="edit-hold-due-date">
                  <Form.Label className="required">
                    Delivery/Cycle Date
                  </Form.Label>
                  <DatePicker
                    id="edit-hold-due-date"
                    selected={form.dueDate}
                    onChange={date => setField('dueDate', date)}
                    dateFormat="MM/dd/yy"
                    placeholderText="Select Due Date"
                    className={`form-control${
                      errors.dueDate ? ' is-invalid' : ''
                    }`}
                    disabled={isSaving}
                    popperPlacement="bottom-start"
                  />
                  {errors.dueDate && (
                    <div className="invalid-feedback d-block">
                      {errors.dueDate}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="edit-hold-reason">
                  <Form.Label className="required">Hold Reason</Form.Label>
                  <Form.Select
                    value={form.reason}
                    isInvalid={Boolean(errors.reason)}
                    disabled={isSaving}
                    onChange={e => setField('reason', e.target.value)}
                  >
                    <option value="">Select Reason</option>
                    {reasonOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.reason}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onHide} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {isSaving ? <Spinner size="sm" animation="border" /> : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal
        centered
        show={show && confirming}
        onHide={() => setConfirming(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Save these changes to the hold for Rx {hold?.rx_id}?
          </p>
          {summary.map(row => (
            <div
              key={row.label}
              className="d-flex justify-content-between gap-3 py-2 border-top border-200"
            >
              <span className="text-600">{row.label}</span>
              {row.from === row.to ? (
                <span className="text-900">{row.to}</span>
              ) : (
                <span className="text-end">
                  <span className="text-500">{row.from}</span>
                  <span className="text-500 mx-2">→</span>
                  <span className="fw-semibold text-900">{row.to}</span>
                </span>
              )}
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

EditHoldModal.propTypes = {
  show: PropTypes.bool.isRequired,
  hold: PropTypes.shape({
    hold_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rx_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    drug: PropTypes.string,
    patient: PropTypes.string,
    qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    raw_due_date: PropTypes.string,
    holdreason_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reason: PropTypes.string
  }),
  // Active hold reasons as { key: label, value: id }.
  reasons: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  onHide: PropTypes.func.isRequired,
  // Resolves to an error message to show, or '' once saved.
  onSave: PropTypes.func.isRequired
};

export default EditHoldModal;
